import React, { useState, useEffect } from 'react';
import { Building2, Store, Users, Sparkles, MapPin } from 'lucide-react';

const IndiaMap3D = () => {
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Accurate India Map Image URL
  const indiaMapUrl = "/images/india-map.png";

  // Location data with percentage coordinates mapped to the India map image
  // These coordinates are calibrated to match the actual India map
  // Noida is in North-Central India, slightly east of center
  const locations = {
    headOffice: [
      { id: 'hq', name: 'Noida', state: 'Uttar Pradesh', x: 47, y: 36, info: 'Corporate Headquarters' }
    ],
    majorHubs: [
      { id: 'mh1', name: 'Mumbai', state: 'Maharashtra', x: 28, y: 58, distributors: 15 },
      { id: 'mh2', name: 'Kolkata', state: 'West Bengal', x: 70, y: 48, distributors: 12 },
      { id: 'mh3', name: 'Ahmedabad', state: 'Gujarat', x: 24, y: 45, distributors: 10 },
      { id: 'mh4', name: 'Chennai', state: 'Tamil Nadu', x: 50, y: 78, distributors: 14 },
      { id: 'mh5', name: 'Bangalore', state: 'Karnataka', x: 42, y: 72, distributors: 11 },
      { id: 'mh6', name: 'Hyderabad', state: 'Telangana', x: 45, y: 60, distributors: 8 },
    ],
    regionalHubs: [
      { id: 'rh1', name: 'Delhi NCR', state: 'Delhi', x: 44, y: 32, distributors: 6 },
      { id: 'rh2', name: 'Jaipur', state: 'Rajasthan', x: 36, y: 38, distributors: 4 },
      { id: 'rh3', name: 'Pune', state: 'Maharashtra', x: 32, y: 62, distributors: 5 },
      { id: 'rh4', name: 'Surat', state: 'Gujarat', x: 26, y: 52, distributors: 3 },
      { id: 'rh5', name: 'Lucknow', state: 'U.P.', x: 52, y: 38, distributors: 3 },
      { id: 'rh6', name: 'Chandigarh', state: 'Punjab', x: 42, y: 26, distributors: 2 },
      { id: 'rh7', name: 'Kochi', state: 'Kerala', x: 40, y: 82, distributors: 3 },
      { id: 'rh8', name: 'Guwahati', state: 'Assam', x: 82, y: 38, distributors: 2 },
    ],
    localDistributors: [
      { id: 'ld1', name: 'Indore', state: 'M.P.', x: 36, y: 50 },
      { id: 'ld2', name: 'Bhopal', state: 'M.P.', x: 42, y: 48 },
      { id: 'ld3', name: 'Nagpur', state: 'Maharashtra', x: 48, y: 54 },
      { id: 'ld4', name: 'Coimbatore', state: 'Tamil Nadu', x: 44, y: 80 },
      { id: 'ld5', name: 'Vizag', state: 'A.P.', x: 58, y: 60 },
      { id: 'ld6', name: 'Patna', state: 'Bihar', x: 58, y: 40 },
      { id: 'ld7', name: 'Raipur', state: 'Chhattisgarh', x: 52, y: 52 },
      { id: 'ld8', name: 'Bhubaneswar', state: 'Odisha', x: 60, y: 56 },
      { id: 'ld9', name: 'Ranchi', state: 'Jharkhand', x: 58, y: 46 },
      { id: 'ld10', name: 'Dehradun', state: 'Uttarakhand', x: 44, y: 28 },
      { id: 'ld11', name: 'Jammu', state: 'J&K', x: 40, y: 18 },
      { id: 'ld12', name: 'Thiruvananthapuram', state: 'Kerala', x: 42, y: 88 },
      { id: 'ld13', name: 'Madurai', state: 'Tamil Nadu', x: 46, y: 82 },
      { id: 'ld14', name: 'Varanasi', state: 'U.P.', x: 54, y: 40 },
      { id: 'ld15', name: 'Agra', state: 'U.P.', x: 46, y: 36 },
    ]
  };

  const getMarkerStyle = (type) => {
    switch (type) {
      case 'headOffice':
        return { 
          size: 'w-5 h-5 md:w-6 md:h-6', 
          bg: '#FF4D00', 
          glow: 'shadow-lg shadow-orange-500/60',
          pulse: true 
        };
      case 'majorHub':
        return { 
          size: 'w-3.5 h-3.5 md:w-4 md:h-4', 
          bg: '#3B82F6', 
          glow: 'shadow-lg shadow-blue-500/50',
          pulse: true 
        };
      case 'regionalHub':
        return { 
          size: 'w-2.5 h-2.5 md:w-3 md:h-3', 
          bg: '#10B981', 
          glow: 'shadow-md shadow-green-500/40',
          pulse: false 
        };
      case 'localDistributor':
        return { 
          size: 'w-2 h-2 md:w-2.5 md:h-2.5', 
          bg: '#FBBF24', 
          glow: 'shadow-sm shadow-yellow-500/30',
          pulse: false 
        };
      default:
        return {};
    }
  };

  const renderMarker = (location, type) => {
    const style = getMarkerStyle(type);
    const isHovered = hoveredLocation?.id === location.id;
    
    return (
      <div
        key={location.id}
        className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10 group"
        style={{ left: `${location.x}%`, top: `${location.y}%` }}
        onMouseEnter={() => setHoveredLocation({ ...location, type })}
        onMouseLeave={() => setHoveredLocation(null)}
      >
        {/* Pulse Animation */}
        {style.pulse && (
          <div 
            className={`absolute inset-0 ${style.size} rounded-full animate-ping opacity-40`}
            style={{ backgroundColor: style.bg }}
          ></div>
        )}
        
        {/* Marker */}
        <div 
          className={`relative ${style.size} rounded-full ${style.glow} transition-all duration-300 ${isHovered ? 'scale-150 z-30' : 'scale-100'}`}
          style={{ backgroundColor: style.bg }}
        >
          {/* Inner shine */}
          <div className="absolute inset-0.5 rounded-full bg-white/20"></div>
        </div>

        {/* Tooltip */}
        <div 
          className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 transition-all duration-200 z-50
            ${isHovered ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}
          `}
        >
          <div className="bg-white rounded-xl shadow-2xl px-4 py-3 min-w-[150px] border border-neutral-100 whitespace-nowrap">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: style.bg }}></div>
              <span className="font-bold text-neutral-800 text-sm">{location.name}</span>
            </div>
            <p className="text-xs text-neutral-500 mb-1">{location.state}</p>
            <div className={`text-xs font-semibold ${
              type === 'headOffice' ? 'text-orange-600' :
              type === 'majorHub' ? 'text-blue-600' :
              type === 'regionalHub' ? 'text-green-600' :
              'text-amber-600'
            }`}>
              {type === 'headOffice' ? '🏢 ' + location.info :
               type === 'majorHub' ? `📦 ${location.distributors} Distributors` :
               type === 'regionalHub' ? `🏪 ${location.distributors} Distributors` :
               '📍 Local Partner'}
            </div>
            {/* Arrow */}
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-r border-b border-neutral-100"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 md:py-20 relative overflow-hidden" data-testid="india-map-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-[#0a1525] to-slate-900"></div>
      
      <div className="relative max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 text-sm font-semibold tracking-wide">NATIONWIDE PRESENCE</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            PAN India <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">Distributor Network</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base md:text-lg">
            From Kashmir to Kanyakumari, delivering premium mobile accessories through our trusted network of 100+ distributors across 28 states.
          </p>
        </div>

        {/* 3D Map Container */}
        <div className="relative max-w-3xl mx-auto" style={{ perspective: '1200px' }}>
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-gradient-radial from-blue-500/15 via-purple-500/5 to-transparent blur-[60px] rounded-full"></div>
          
          {/* Map Card with 3D Transform */}
          <div 
            className={`relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              transform: isVisible ? 'rotateX(5deg)' : 'rotateX(15deg)',
              transformStyle: 'preserve-3d'
            }}
          >
            {/* 3D Shadow */}
            <div 
              className="absolute inset-0 bg-black/40 rounded-3xl blur-2xl"
              style={{ transform: 'translateZ(-40px) translateY(25px) scale(0.97)' }}
            ></div>
            
            {/* Main Card */}
            <div className="relative bg-gradient-to-br from-slate-800/90 via-slate-850/85 to-slate-900/95 rounded-2xl md:rounded-3xl border border-slate-700/40 backdrop-blur-xl overflow-hidden">
              {/* Top border glow */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
              
              {/* Map Container */}
              <div className="relative p-4 md:p-8">
                {/* India Map Image with Markers Overlay */}
                <div className="relative mx-auto" style={{ maxWidth: '550px' }}>
                  {/* India Map Background Image */}
                  <img 
                    src={indiaMapUrl}
                    alt="India Map"
                    className="w-full h-auto"
                    style={{ 
                      filter: 'drop-shadow(0 0 30px rgba(59, 130, 246, 0.3))',
                    }}
                  />
                  
                  {/* Markers Overlay */}
                  <div className="absolute inset-0">
                    {/* Connection Lines from HQ to Major Hubs */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
                          <stop offset="100%" stopColor="#f97316" stopOpacity="0.6" />
                        </linearGradient>
                      </defs>
                      {locations.majorHubs.map((hub, i) => (
                        <line 
                          key={`line-${hub.id}`}
                          x1={locations.headOffice[0].x}
                          y1={locations.headOffice[0].y}
                          x2={hub.x}
                          y2={hub.y}
                          stroke="url(#lineGradient)"
                          strokeWidth="0.3"
                          strokeDasharray="2,1"
                          className={`transition-opacity duration-1000 ${isVisible ? 'opacity-50' : 'opacity-0'}`}
                          style={{ transitionDelay: `${i * 150 + 500}ms` }}
                        />
                      ))}
                    </svg>

                    {/* Location Markers */}
                    {locations.headOffice.map(loc => renderMarker(loc, 'headOffice'))}
                    {locations.majorHubs.map(loc => renderMarker(loc, 'majorHub'))}
                    {locations.regionalHubs.map(loc => renderMarker(loc, 'regionalHub'))}
                    {locations.localDistributors.map(loc => renderMarker(loc, 'localDistributor'))}
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-2 md:gap-4">
                  <div className="flex items-center gap-2 bg-slate-700/40 px-3 py-2 md:px-4 md:py-2 rounded-full border border-slate-600/30">
                    <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#FF4D00] shadow-lg shadow-orange-500/50"></div>
                    <span className="text-slate-200 text-xs md:text-sm font-medium">Head Office</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-700/40 px-3 py-2 md:px-4 md:py-2 rounded-full border border-slate-600/30">
                    <div className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 rounded-full bg-[#3B82F6] shadow-lg shadow-blue-500/50"></div>
                    <span className="text-slate-200 text-xs md:text-sm font-medium">Major Hub</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-700/40 px-3 py-2 md:px-4 md:py-2 rounded-full border border-slate-600/30">
                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#10B981]"></div>
                    <span className="text-slate-200 text-xs md:text-sm font-medium">Regional Hub</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-700/40 px-3 py-2 md:px-4 md:py-2 rounded-full border border-slate-600/30">
                    <div className="w-1.5 h-1.5 md:w-2.5 md:h-2.5 rounded-full bg-[#FBBF24]"></div>
                    <span className="text-slate-200 text-xs md:text-sm font-medium">Local Partner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { value: '100+', label: 'Distributors', icon: Building2, color: 'from-blue-500 to-blue-600' },
              { value: '28', label: 'States & UTs', icon: MapPin, color: 'from-green-500 to-emerald-600' },
              { value: '5000+', label: 'Retail Partners', icon: Store, color: 'from-purple-500 to-violet-600' },
              { value: '10K+', label: 'Happy Customers', icon: Users, color: 'from-orange-500 to-red-500' }
            ].map((stat, i) => (
              <div 
                key={i}
                className={`relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl md:rounded-2xl p-4 md:p-5 border border-slate-700/30 
                  backdrop-blur-sm overflow-hidden group hover:border-slate-600/50 transition-all duration-300
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: `${i * 100 + 600}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className="relative">
                  <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-slate-500 mb-2 group-hover:text-slate-400 transition-colors" />
                  <div className={`text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                    {stat.value}
                  </div>
                  <div className="text-slate-400 text-xs md:text-sm mt-1">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-8 md:mt-12 text-center">
          <div className="inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-slate-800/50 to-slate-800/30 px-4 md:px-6 py-2 md:py-3 rounded-full border border-slate-700/30">
            <span className="text-xl md:text-2xl">🇮🇳</span>
            <span className="text-slate-300 text-sm md:text-base font-medium">Proudly Made in India</span>
            <span className="text-slate-600 hidden md:inline">•</span>
            <span className="text-slate-400 text-sm hidden md:inline">Serving Since 2019</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndiaMap3D;
